import useSWR, { type SWRConfiguration, type SWRResponse } from 'swr';

import type { Fetcher, SerializableRecord } from './types';

export const REQUEST_URL = `/gateway/api/post-office/api/v1/placements/`;

const isNotEmpty = (input: unknown) => !(typeof input === 'undefined' || input === null);

const stringifyObjectValues = <T extends Record<string, unknown>>(input: T) => {
	const entries = Object.entries(input);
	const stringifiedEntries = entries
		.filter(([, value]) => isNotEmpty(value))
		.map(([key, value]) => [key, String(value)]);
	return Object.fromEntries(stringifiedEntries) as Record<string, string>;
};

const defaultHeaders = {
	credentials: 'include',
	mode: 'cors',
	'Content-Type': 'application/json',
};

export const useFetchPlacementData = <
	TData extends SerializableRecord,
	TContext extends SerializableRecord,
>(
	placementId: string | null, // null if disabled
	path: string,
	context: TContext,
	fetcher: Fetcher,
	requestOptions?: RequestInit,
	useSWROptions?: SWRConfiguration,
): SWRResponse<TData, Error> => {
	const normalizedPath =
		path === '' || path === '/' ? '' : `/${path.charAt(0) === '/' ? path.substring(1) : path}`;

	const queryParams = new URLSearchParams(stringifyObjectValues(context));
	queryParams.sort();

	const contextAsSearchParams = queryParams.toString();

	const formattedSearchParams = contextAsSearchParams
		? `?${contextAsSearchParams}`
		: contextAsSearchParams;

	const url = encodeURI(`${REQUEST_URL}${placementId}${normalizedPath}${formattedSearchParams}`);

	return useSWR(
		placementId ? url : null,
		(url) =>
			fetcher
				.fetch(url, {
					...requestOptions,
					headers: {
						...defaultHeaders,
						...requestOptions?.headers,
					},
				})
				?.then((res) => {
					if (!res) {
						throw new Error('No response from fetch');
					}

					return res.json();
				}),
		useSWROptions,
	);
};
